import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Trophy, Heart, Zap, ShieldCheck, Target, Users, ArrowRight } from "lucide-react";
import ClubCard from "../components/ClubCard";
import PageMeta from "../components/PageMeta";
import InstallAppCard from "../components/InstallAppCard";

const HERO_BG =
    "https://images.unsplash.com/photo-1772707681004-ebbce15554d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxzb2NjZXIlMjBwbGF5ZXIlMjBzdGFkaXVtJTIwbmlnaHR8ZW58MHx8fHwxNzc3MTg5MDY4fDA&ixlib=rb-4.1.0&q=85";
const STATS_BG =
    "https://images.pexels.com/photos/1657322/pexels-photo-1657322.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
    const [stats, setStats] = useState({ delivered: 0, in_production: 0, clubs: 0, sponsors: 0, total_collected: 0 });
    const [clubs, setClubs] = useState([]);

    useEffect(() => {
        api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/clubs").then((r) => setClubs((r.data.items || []).slice(0, 3))).catch(() => {});
    }, []);

    return (
        <div className="grain">
            <PageMeta
                title="Sponsormania.fr — Micro-sponsoring sportif | 0% commission"
                description="La plateforme solidaire qui relie clubs amateurs et sponsors locaux pour financer les maillots de sport. Service gratuit, 0% commission. Football, basketball, rugby, handball."
            />
            {/* HERO */}
            <section className="relative min-h-[88vh] flex items-end overflow-hidden border-b border-zinc-900">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/65 to-[#09090b]" />
                <div className="absolute inset-0 bg-[#09090b]/40" />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24 w-full">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-orange-500/40 bg-orange-500/10 text-orange-400 text-[11px] uppercase tracking-[0.2em] font-bold mb-6">
                        <Zap size={12} /> 0% Commission · Service Gratuit
                    </div>
                    <h1 className="font-display text-6xl sm:text-8xl lg:text-9xl uppercase leading-[0.85] tracking-tighter max-w-5xl">
                        Financez les <span className="text-orange-500">maillots</span>
                        <br />
                        des clubs amateurs.
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-zinc-300">
                        Place de marché solidaire entre clubs de sport et sponsors. Un maillot, plusieurs logos,
                        zéro commission. Dès <span className="font-bold text-orange-500">19€ HT</span> collectés
                        par maillot, on lance la fabrication.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-4">
                        <Link to="/clubs" data-testid="hero-cta-sponsor" className="btn-primary">
                            <Trophy size={18} /> Sponsoriser un club
                        </Link>
                        <Link to="/register" data-testid="hero-cta-club" className="btn-ghost">
                            <Heart size={18} /> Inscrire mon club
                        </Link>
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Link
                            to="/panther"
                            data-testid="panther-partnership-badge"
                            className="inline-flex items-center gap-2 border border-zinc-800 bg-zinc-950/70 hover:border-orange-500/60 hover:bg-zinc-900 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-zinc-300 transition-colors"
                        >
                            <span className="w-1.5 h-1.5 bg-orange-500" />
                            En partenariat avec la marque <span className="text-orange-500 font-bold">Panther</span>
                            <ArrowRight size={12} className="text-orange-500" />
                        </Link>
                        <a
                            href="https://www.mypanther.fr"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="panther-external-link"
                            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-orange-500 transition-colors"
                        >
                            mypanther.fr ↗
                        </a>
                    </div>
                </div>
            </section>

            {/* STATS BAND */}
            <section id="stats" className="relative border-b border-zinc-900">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-15"
                    style={{ backgroundImage: `url(${STATS_BG})` }}
                />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800">
                    {[
                        { label: "Maillots offerts", val: stats.delivered + stats.in_production, sub: "Fabriqués / livrés" },
                        { label: "Clubs actifs", val: stats.clubs, sub: "Sur la plateforme" },
                        { label: "Sponsors", val: stats.sponsors, sub: "Engagés" },
                        { label: "Collectés", val: `${stats.total_collected}€`, sub: "0€ commission" },
                    ].map((s) => (
                        <div key={s.label} className="bg-[#09090b] p-6 md:p-8">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">
                                {s.label}
                            </div>
                            <div className="font-display text-5xl md:text-6xl text-orange-500 leading-none">
                                {s.val}
                            </div>
                            <div className="text-xs text-zinc-500 mt-2">{s.sub}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                            Comment ça marche
                        </div>
                        <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tight leading-none max-w-2xl">
                            3 étapes pour habiller un club
                        </h2>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    {[
                        {
                            n: "01",
                            t: "Le club s'inscrit",
                            d: "Création de la fiche club : sport, couleurs, catégories d'âge, effectif, besoins.",
                            icon: <Users />,
                            cls: "md:col-span-5",
                        },
                        {
                            n: "02",
                            t: "Les sponsors choisissent",
                            d: "Sélection visuelle des emplacements (poitrine, dos, épaules) et upload du logo HD.",
                            icon: <Target />,
                            cls: "md:col-span-7",
                        },
                        {
                            n: "03",
                            t: "Production dès 19€",
                            d: "Une fois le seuil atteint et la sélection validée par le club, le maillot part en fabrication.",
                            icon: <ShieldCheck />,
                            cls: "md:col-span-12",
                        },
                    ].map((s) => (
                        <div key={s.n} className={`card-tu ${s.cls} flex gap-6 items-start`}>
                            <div className="text-orange-500">{s.icon}</div>
                            <div>
                                <div className="font-display text-5xl text-zinc-700 leading-none">{s.n}</div>
                                <h3 className="font-display text-3xl tracking-tight uppercase mt-2">{s.t}</h3>
                                <p className="text-zinc-400 mt-2">{s.d}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURED CLUBS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                            Ils ont besoin de vous
                        </div>
                        <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tight">
                            Clubs à la une
                        </h2>
                    </div>
                    <Link
                        to="/clubs"
                        data-testid="see-all-clubs"
                        className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-widest font-bold text-orange-500"
                    >
                        Voir tous les clubs <ArrowRight size={16} />
                    </Link>
                </div>
                {clubs.length === 0 ? (
                    <div className="card-tu text-center text-zinc-500">
                        Aucun club pour le moment.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {clubs.map((c) => (
                            <ClubCard key={c.id} club={c} />
                        ))}
                    </div>
                )}
            </section>

            <InstallAppCard />

            {/* FINAL CTA */}
            <section className="border-t border-zinc-900 bg-gradient-to-br from-orange-600 to-red-700 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22><path d=%22M0 0h100v100H0z%22 fill=%22none%22/><path d=%22M0 50h100M50 0v100%22 stroke=%22%23000%22 stroke-opacity=%22.05%22/></svg>')]" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 text-center">
                    <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tight text-white">
                        Et si on lançait votre maillot&nbsp;?
                    </h2>
                    <p className="mt-4 text-white/85 max-w-2xl mx-auto">
                        Rejoignez la première marketplace solidaire de sponsoring sportif.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-4">
                        <Link
                            to="/register"
                            data-testid="cta-final-register"
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-black text-white font-bold uppercase tracking-widest hover:bg-zinc-900 transition-colors"
                        >
                            Créer un compte
                        </Link>
                        <Link
                            to="/clubs"
                            data-testid="cta-final-explore"
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur text-white font-bold uppercase tracking-widest border-2 border-white/30 hover:bg-white/20 transition-colors"
                        >
                            Explorer les clubs
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
